import { millisecondsToSeconds } from 'date-fns';
import { action, computed, makeObservable, observable } from 'mobx';
import { hasValue } from 'src/shared/utils/common';
import { equalsDeep } from 'src/shared/utils/equals-deep';

import { TConstantPadValues, TPadReadiness, TPadValues, TPreparationStatus, TRawPadPreparation } from '../types';
import { getConstAndEditablePadValues } from '../utils';

import { RigSiteEvent } from './rig-site-event.entity';

const getReadinessStatusByDates = (currentDate: number, date: number | null): TPreparationStatus => {
  if (!hasValue(date)) {
    return 'notReady';
  }

  if (currentDate >= date) {
    return 'ready';
  }

  return 'inProcess';
};

export class PadPreparation {
  readonly constantValues: TConstantPadValues;
  readonly initialValues: TPadValues;
  readonly id: number;

  readonly rseMap = new Map<string, RigSiteEvent>();
  @observable _rigSiteEvents: RigSiteEvent[] = [];
  @observable values: TPadValues;

  constructor(initialPad: TRawPadPreparation) {
    const { constant, editable } = getConstAndEditablePadValues(initialPad);
    this.constantValues = constant;
    this.initialValues = editable;
    this.values = editable;
    this.id = initialPad.padId;

    makeObservable(this);
  }

  @computed
  get hasChanges(): boolean {
    return equalsDeep(this.initialValues, this.values);
  }

  @computed
  get rigSiteEvents(): RigSiteEvent[] {
    return [...this._rigSiteEvents].sort((a, b) => {
      const aMobDate = a.values.mobilizationStartDate;
      const bMobDate = b.values.mobilizationStartDate;

      if (!hasValue(aMobDate) && !hasValue(bMobDate)) {
        return 0;
      }

      if (!hasValue(bMobDate)) {
        return -1;
      }

      if (!hasValue(aMobDate)) {
        return 1;
      }

      return aMobDate - bMobDate;
    });
  }

  @computed
  get blankRSE(): RigSiteEvent | null {
    if (this.rigSiteEvents.length) {
      return null;
    }

    return this._rigSiteEvents.find((rse) => rse.id === 'blank') ?? null;
  }

  @computed
  get updatedRSE(): RigSiteEvent[] {
    return this._rigSiteEvents.filter((rse) => rse.hasChanges);
  }

  @computed
  get firstRse(): RigSiteEvent | null {
    return (
      this.rigSiteEvents
        .filter((rse) => hasValue(rse.values.padReadyDate))
        .sort((a, b) => a.values.padReadyDate! - b.values.padReadyDate!)[0] ?? null
    );
  }

  @computed
  get padReadiness(): TPadReadiness {
    const currentUnixTime = millisecondsToSeconds(Date.now());
    const { landLeaseDate, forestProjectDate, routeReadyDate, routeDocumentationDate, electricityDate } = this.values;

    const padReady: TPreparationStatus = (() => {
      let status: TPreparationStatus = 'notReady';

      for (const rse of this.rigSiteEvents) {
        if (rse.RSEReadiness.padReady === 'ready') {
          status = rse.RSEReadiness.padReady;
          break;
        }

        if (rse.RSEReadiness.padReady === 'inProcess') {
          status = rse.RSEReadiness.padReady;
        }

        continue;
      }

      return status;
    })();
    const landReady: TPreparationStatus = (() => {
      const landReady = getReadinessStatusByDates(currentUnixTime, landLeaseDate);
      const forestReady = getReadinessStatusByDates(currentUnixTime, forestProjectDate);

      const _ = [landReady, forestReady] as const;

      if (_.includes('notReady')) {
        return 'notReady';
      } else if (_.includes('inProcess')) {
        return 'inProcess';
      }

      return 'ready';
    })();
    const routeReady = getReadinessStatusByDates(currentUnixTime, routeReadyDate);
    const routeDocumentationReady = getReadinessStatusByDates(currentUnixTime, routeDocumentationDate);
    const electricityReady = getReadinessStatusByDates(currentUnixTime, electricityDate);

    return {
      padReady,
      landReady,
      routeReady,
      routeDocumentationReady,
      electricityReady,
    };
  }

  @action.bound
  addRSE(rse: RigSiteEvent): void {
    this.rseMap.set(rse.id, rse);
    this._rigSiteEvents.push(rse);
  }

  @action.bound
  updateValues(values: Partial<TPadValues>): void {
    this.values = { ...this.values, ...values };
  }

  copySelf = (): PadPreparation => {
    const copy = new PadPreparation({ ...this.constantValues, ...this.initialValues });

    this._rigSiteEvents.forEach((rse) => {
      copy.addRSE(rse.copySelf());
    });

    return copy;
  };
}
